import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import WaveUIProgressDialog from '@/shared/components/dialogs/WaveUIProgressDialog';
import WaveUINotificationBar from '@/shared/components/utilities/WaveUINotificationBar';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import DialogName from '@/shared/types/dialogName.enum';
import PaymentProcessor from '@/shared/types/order/paymentProcessor.type';

import PaymentProcessorDialog from './components/AddPaymentProcessorDialog';
import ApiKeyViewDialog from './components/ApiKeyViewDialog';
import PaymentProcessorsTable_v2 from './components/PaymentProcessorsTable_v2/PaymentProcessorsTable_v2';
import usePaymentProcessorsOperations from './hooks/usePaymentProcessorsOperations';

const PaymentProcessors: FC = () => {
  const { t } = useTranslation(['paymentProcessors', 'common']);

  const { dialog, onUpdateDialogState } = useModalDialog<unknown, DialogName>();
  const { progressDialog, onUpdateProgressDialog } = useProgressDialog();
  const { notificationBar, onUpdateNotificationBar } = useNotificationBar();

  const {
    refreshTableIndex,
    supportedPaymentProcessors,
    onAddPaymentProcessor,
    onUpdatePaymentProcessor,
  } = usePaymentProcessorsOperations({
    onUpdateNotificationBar,
    onUpdateProgressDialog,
    onUpdateDialogState,
  });

  return (
    <div>
      <PaymentProcessorsTable_v2
        refreshTableIndex={refreshTableIndex}
        supportedPaymentProcessors={supportedPaymentProcessors}
        openViewApiKeyDialog={(key) => {
          onUpdateDialogState(DialogName.ViewApiKey, {
            open: true,
            data: key,
          });
        }}
        openAddPaymentProcessorDialog={() =>
          onUpdateDialogState(DialogName.AddPaymentProcessor, { open: true })
        }
        openEditPaymentProcessorDialog={(processor) =>
          onUpdateDialogState(DialogName.EditPaymentProcessor, {
            open: true,
            data: processor,
          })
        }
      />

      {dialog?.viewApiKey?.open && (
        <ApiKeyViewDialog
          apiKey={dialog.viewApiKey.data as string}
          onClose={() =>
            onUpdateDialogState(DialogName.ViewApiKey, { open: false })
          }
        />
      )}

      {dialog?.addPaymentProcessor?.open && supportedPaymentProcessors && (
        <PaymentProcessorDialog
          errors={dialog.addPaymentProcessor.errors}
          title={t('paymentProcessors:addPaymentProcessor')}
          supportedPaymentProcessors={supportedPaymentProcessors}
          onClose={() =>
            onUpdateDialogState(DialogName.AddPaymentProcessor, { open: false })
          }
          proceed={(processor) => {
            void onAddPaymentProcessor(processor);
          }}
        />
      )}

      {dialog?.editPaymentProcessor?.open && supportedPaymentProcessors && (
        <PaymentProcessorDialog
          errors={dialog.editPaymentProcessor.errors}
          title={t('paymentProcessors:editPaymentProcessor')}
          processor={dialog.editPaymentProcessor.data as PaymentProcessor}
          supportedPaymentProcessors={supportedPaymentProcessors}
          onClose={() =>
            onUpdateDialogState(DialogName.EditPaymentProcessor, {
              open: false,
            })
          }
          proceed={(processor, processorId) => {
            void onUpdatePaymentProcessor(processor, processorId as string);
          }}
        />
      )}

      <WaveUINotificationBar
        message={notificationBar.message}
        severity={notificationBar.severity}
        open={notificationBar.open}
        close={() => onUpdateNotificationBar({ open: false })}
      />

      <WaveUIProgressDialog
        title={progressDialog?.title}
        progress={progressDialog?.progress || 0}
        open={progressDialog?.open || false}
        onClose={() => onUpdateProgressDialog({ open: false })}
      />
    </div>
  );
};

export default PaymentProcessors;
